<template>
  <div
    v-if="visible"
    class="practice_guide"
    :class="{ coach: isCoachStep }"
  >
    <div v-if="showDim" class="practice_guide_dim"></div>
    <div
      v-if="targetRect"
      class="practice_guide_highlight"
      :style="highlightStyle"
    ></div>
    <div
      ref="card"
      class="practice_guide_card"
      :class="{ center: !targetRect }"
      :style="cardStyle"
      @click.stop
    >
      <div class="guide_progress">新手引导 {{ currentIndex + 1 }} / {{ steps.length }}</div>
      <div class="guide_title">{{ currentStep.title }}</div>
      <div class="guide_text">{{ currentStep.text }}</div>
      <div class="guide_actions">
        <button
          type="button"
          class="guide_cancel"
          @click="cancelStudy"
        >
          取消学习
        </button>
        <button
          type="button"
          class="guide_next"
          :class="{ disabled: !canUseNext }"
          :disabled="!canUseNext"
          @click="next"
        >
          {{ nextButtonText }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
const GUIDE_STORAGE_KEY = "practice_editor_guide_v1_done";

export default {
  data() {
    return {
      visible: false,
      currentIndex: 0,
      targetRect: null,
      cardHeight: 176,
      completedKeys: {},
      dragFocusRect: null,
      measureTimer: null,
      steps: [
        {
          key: "materialCatalog",
          title: "添加容器/花材",
          text: "点击这里，打开素材库，先添加容器、再添加花材",
          target: '[data-guide="material-catalog"]',
          requirements: [
            {
              key: "materialOpen",
              label: "打开素材库",
            },
          ],
          waitText: "请点击高亮按钮",
          autoAdvance: true,
        },
        {
          key: "container",
          title: "第一步：选容器",
          text: "点击任意容器卡片，容器会自动进入画布中。",
          target: [
            '[data-guide="container-first-card"]',
            '[data-guide="material-container-tab"]',
          ],
          materialType: 2,
          requirements: [
            {
              key: "containerAdd",
              label: "放入一个容器",
              target: '[data-guide="container-first-card"]',
            },
          ],
          waitText: "请选择一个容器",
          autoAdvance: true,
        },
        {
          key: "flower",
          title: "第二步：选花材",
          text: "点击花材卡片，花材会自动加载进入画布。",
          target: [
            '[data-guide="flower-first-card"]',
            '[data-guide="material-flower-tab"]',
          ],
          materialType: 1,
          requirements: [
            {
              key: "flowerAdd",
              label: "放入一支花材",
              target: '[data-guide="flower-first-card"]',
            },
          ],
          waitText: "请选择一个花材",
          afterComplete() {
            this.$bus.$emit("guide-close-material");
          },
          autoAdvance: true,
        },
        {
          key: "drag",
          title: "第三步：拖动调整",
          text: "按住花材焦点，进行拖拽。",
          target: "selection-helper",
          requirements: [
            {
              key: "flowerMove",
              label: "拖动花材",
              target: "selection-helper",
            },
          ],
          waitText: "请按住拖拽点移动花材",
          closeMaterial: true,
          enableFocus: true,
          coach: true,
          cardPlacement: "target-bottom-right",
        },
        {
          key: "focus",
          title: "第四步：焦点",
          text: "点击焦点按钮隐藏或开启焦点，焦点让您更准确的拖动素材",
          target: '[data-guide="tool-focus"]',
          toolIndex: 6,
          requirements: [
            {
              key: "tool-6",
              label: "切换一次焦点",
            },
          ],
          waitText: "请点击焦点按钮",
          afterComplete() {
            setTimeout(() => {
              this.$bus.$emit("setTouchHelper", true);
            }, 360);
          },
          coach: true,
          cardPlacement: "above",
        },
        {
          key: "bend",
          title: "第五步：弯曲",
          text: "拖动单支弯曲滑杆，让花材调整到合适角度。",
          target: [
            '[data-guide="bend-single-control"]',
            '[data-guide="tool-bend"]',
          ],
          openTool: 0,
          requirements: [
            {
              key: "bend",
              label: "拖动单枝滑杆",
              target: '[data-guide="bend-single-control"]',
            },
          ],
          waitText: "请拖动单枝滑杆",
          coach: true,
        },
        {
          key: "flowerHead",
          title: "第六步：花头调整",
          text: "可以调整花头大小，和花头的朝向。",
          target: '[data-guide="flower-head-panel"]',
          openTool: 1,
          requirements: [
            {
              key: "flowerHeadSize",
              label: "拖动花头大小",
              target: '[data-guide="flower-head-size-control"]',
              waitText: "请先拖动大小滑杆",
            },
            {
              key: "flowerHeadDirection",
              label: "点击左右箭头调朝向",
              target: '[data-guide="flower-head-direction-control"]',
              waitText: "再点击一次左右箭头",
            },
          ],
          waitText: "请先拖动大小，再点击左右箭头",
          coach: true,
        },
        {
          key: "containerTool",
          title: "第七步：容器调整",
          text: "拖动滑杆调整容器大小，开启器皿光感，感受容器质感变化。",
          target: [
            '[data-guide="container-size-control"]',
            '[data-guide="tool-container"]',
          ],
          openTool: 9,
          requirements: [
            {
              key: "containerSize",
              label: "拖动容器大小",
              target: '[data-guide="container-size-control"]',
            },
            {
              key: "containerLight",
              label: "开启器皿光感",
              target: '[data-guide="container-light-control"]',
            },
          ],
          waitText: "请拖动容器大小滑杆",
          coach: true,
        },
        {
          key: "layer",
          title: "第八步：图层管理",
          text: "画布中素材会显示在图层中，可快速找到花束中花材进行调整。",
          target: [
            '[data-guide="layer-first-eye"]',
            '[data-guide="layer-first-item"]',
            '[data-guide="layer-panel"]',
            '[data-guide="tool-layer"]',
          ],
          showLayer: true,
          closeTool: true,
          requirements: [
            {
              key: "layer",
              label: "点击图层或眼睛",
              target: [
                '[data-guide="layer-first-eye"]',
                '[data-guide="layer-first-item"]',
                '[data-guide="layer-panel"]',
              ],
            },
          ],
          waitText: "请在左侧图层栏点一次",
          coach: true,
        },
        {
          key: "stem",
          title: "第九步：根茎调整",
          text: "根茎是花头与剑山的连接，可关闭，可调整粗细。",
          target: [
            '[data-guide="stem-control"]',
            '[data-guide="tool-stem"]',
          ],
          openTool: 3,
          requirements: [
            {
              key: "stem",
              label: "调整根茎",
              target: '[data-guide="stem-control"]',
            },
          ],
          waitText: "请操作一次根茎控件",
          coach: true,
        },
      ],
    };
  },
  computed: {
    currentStep() {
      return this.steps[this.currentIndex] || this.steps[0];
    },
    isCoachStep() {
      return !!this.currentStep.coach;
    },
    showDim() {
      return false;
    },
    isLastStep() {
      return this.currentIndex >= this.steps.length - 1;
    },
    currentRequirements() {
      return this.currentStep.requirements || [];
    },
    isTaskStep() {
      return this.currentRequirements.length > 0;
    },
    currentTaskComplete() {
      if (!this.isTaskStep) {
        return true;
      }
      return this.currentRequirements.every((item) => this.completedKeys[item.key]);
    },
    canUseNext() {
      return !this.isTaskStep || this.currentTaskComplete;
    },
    nextButtonText() {
      return this.isLastStep ? "完成" : "下一步";
    },
    activeRequirement() {
      return this.currentRequirements.find((item) => !this.completedKeys[item.key]);
    },
    activeWaitText() {
      return (
        (this.activeRequirement && this.activeRequirement.waitText) ||
        this.currentStep.waitText ||
        "请按高亮位置完成操作"
      );
    },
    highlightStyle() {
      if (!this.targetRect) {
        return {};
      }
      return {
        left: `${this.targetRect.left}px`,
        top: `${this.targetRect.top}px`,
        width: `${this.targetRect.width}px`,
        height: `${this.targetRect.height}px`,
      };
    },
    cardStyle() {
      if (!this.targetRect) {
        return {};
      }
      const viewportWidth = window.innerWidth || 375;
      const viewportHeight = window.innerHeight || 667;
      const cardWidth = Math.min(this.isCoachStep ? 300 : 340, viewportWidth - 32);
      const maxCardHeight = Math.min(this.isCoachStep ? 190 : 320, viewportHeight - 32);
      const cardHeight = Math.min(this.cardHeight || 176, maxCardHeight);
      const gap = 14;
      let left = this.targetRect.left + this.targetRect.width / 2 - cardWidth / 2;
      const spaceBelow = viewportHeight - this.targetRect.bottom - gap - 16;
      const spaceAbove = this.targetRect.top - gap - 16;
      let top = this.targetRect.bottom + gap;

      if (this.currentStep.cardPlacement === "target-bottom-right") {
        left = this.targetRect.right + gap;
        top = this.targetRect.bottom + gap;
        if (left + cardWidth > viewportWidth - 16) {
          left = this.targetRect.left - cardWidth - gap;
        }
        if (top + cardHeight > viewportHeight - 16) {
          top = this.targetRect.top - cardHeight - gap;
        }
        left = Math.max(16, Math.min(left, viewportWidth - cardWidth - 16));
        top = Math.max(16, Math.min(top, viewportHeight - cardHeight - 16));
        return {
          left: `${left}px`,
          top: `${top}px`,
          width: `${cardWidth}px`,
          maxHeight: `${maxCardHeight}px`,
        };
      }

      if (this.currentStep.cardPlacement === "above") {
        top = this.targetRect.top - cardHeight - gap;
        left = Math.max(16, Math.min(left, viewportWidth - cardWidth - 16));
        top = Math.max(16, Math.min(top, viewportHeight - cardHeight - 16));
        return {
          left: `${left}px`,
          top: `${top}px`,
          width: `${cardWidth}px`,
          maxHeight: `${maxCardHeight}px`,
        };
      }

      if (spaceBelow < cardHeight && spaceAbove > spaceBelow) {
        top = this.targetRect.top - cardHeight - gap;
      }

      left = Math.max(16, Math.min(left, viewportWidth - cardWidth - 16));
      top = Math.max(16, Math.min(top, viewportHeight - cardHeight - 16));

      return {
        left: `${left}px`,
        top: `${top}px`,
        width: `${cardWidth}px`,
        maxHeight: `${maxCardHeight}px`,
      };
    },
  },
  mounted() {
    this.bindEvents();
    window.addEventListener("resize", this.updateTargetRect);
    window.addEventListener("scroll", this.updateTargetRect, true);
    setTimeout(() => {
      this.startIfNeeded();
    }, 900);
  },
  beforeDestroy() {
    this.unbindEvents();
    this.stopMeasureLoop();
    window.removeEventListener("resize", this.updateTargetRect);
    window.removeEventListener("scroll", this.updateTargetRect, true);
  },
  methods: {
    bindEvents() {
      this.$bus.$on("practice-guide-start", this.forceStart);
      this.$bus.$on("guide-material-popup-open", this.handleMaterialOpen);
      this.$bus.$on("container-add", this.handleContainerAdded);
      this.$bus.$on("flower-choose", this.handleFlowerChoose);
      this.$bus.$on("flower-move", this.handleFlowerMove);
      this.$bus.$on("guide-tool-click", this.handleToolClick);
      this.$bus.$on("guide-control-change", this.handleControlChange);
      this.$bus.$on("selection-helper-position", this.handleSelectionHelperPosition);
    },
    unbindEvents() {
      this.$bus.$off("practice-guide-start", this.forceStart);
      this.$bus.$off("guide-material-popup-open", this.handleMaterialOpen);
      this.$bus.$off("container-add", this.handleContainerAdded);
      this.$bus.$off("flower-choose", this.handleFlowerChoose);
      this.$bus.$off("flower-move", this.handleFlowerMove);
      this.$bus.$off("guide-tool-click", this.handleToolClick);
      this.$bus.$off("guide-control-change", this.handleControlChange);
      this.$bus.$off("selection-helper-position", this.handleSelectionHelperPosition);
    },
    forceStart() {
      this.start(true);
    },
    getGuideStorageKey() {
      const token = this.$route.query.authToken || localStorage.getItem("authToken") || "anonymous";
      return `${GUIDE_STORAGE_KEY}_${token}`;
    },
    hasCompletedGuide() {
      return localStorage.getItem(this.getGuideStorageKey()) === "1";
    },
    markGuideComplete() {
      localStorage.setItem(this.getGuideStorageKey(), "1");
    },
    startIfNeeded() {
      if (this.$store.state.routeParams.mode !== "edit") {
        return;
      }
      if (this.$route.query.guide === "1" || this.$route.query.guide === "true") {
        this.start(true);
        return;
      }
      if (this.hasCompletedGuide()) {
        return;
      }
      this.start();
    },
    start(force = false) {
      if (!force && this.hasCompletedGuide()) {
        return;
      }
      this.visible = true;
      this.currentIndex = 0;
      this.completedKeys = {};
      this.prepareStep();
      this.startMeasureLoop();
    },
    finish() {
      this.markGuideComplete();
      this.visible = false;
      this.stopMeasureLoop();
    },
    next() {
      if (!this.canUseNext) {
        return;
      }
      this.goNext();
    },
    cancelStudy() {
      this.markGuideComplete();
      this.visible = false;
      this.stopMeasureLoop();
    },
    goNext() {
      if (this.isLastStep) {
        this.finish();
        return;
      }
      this.currentIndex += 1;
      this.completedKeys = {};
      this.prepareStep();
    },
    prepareStep() {
      const step = this.currentStep;
      if (step.closeMaterial) {
        this.$bus.$emit("guide-close-material");
      }
      if (step.closeTool) {
        this.$bus.$emit("guide-close-tool-panel");
      }
      if (step.enableFocus) {
        this.$bus.$emit("setTouchHelper", true);
      }
      if (typeof step.openTool === "number") {
        const toolIndex = step.openTool;
        const stepKey = step.key;
        this.$bus.$emit("guide-open-tool", toolIndex);
        setTimeout(() => {
          if (this.visible && this.currentStep.key === stepKey) {
            this.$bus.$emit("guide-open-tool", toolIndex);
          }
        }, 160);
      }
      if (step.materialType) {
        this.$bus.$emit("guide-open-material", step.materialType);
        setTimeout(() => {
          this.$bus.$emit("guide-select-material-type", step.materialType);
        }, 180);
      }
      if (step.showLayer) {
        this.$bus.$emit("layer-panel-show");
      }
      this.$nextTick(() => {
        this.measureCard();
        this.updateTargetRect();
      });
      setTimeout(this.updateTargetRect, 260);
      setTimeout(this.updateTargetRect, 700);
    },
    completeRequirement(key) {
      if (!this.visible || !key) {
        return;
      }
      const requirement = this.currentRequirements.find((item) => item.key === key);
      if (!requirement || this.completedKeys[key]) {
        return;
      }
      this.$set(this.completedKeys, key, true);
      const action = this.currentStep.afterComplete;
      this.$nextTick(() => {
        this.measureCard();
        this.updateTargetRect();
      });
      if (this.currentTaskComplete) {
        if (action) {
          action.call(this);
        }
        if (this.currentStep.autoAdvance) {
          const stepIndex = this.currentIndex;
          setTimeout(() => {
            if (this.visible && this.currentIndex === stepIndex && this.currentStep.autoAdvance) {
              this.goNext();
            }
          }, this.currentStep.autoAdvanceDelay || 560);
          return;
        }
        this.$nextTick(() => {
          this.measureCard();
          this.updateTargetRect();
        });
      }
    },
    handleMaterialOpen(type) {
      if (!this.visible || this.currentStep.key !== "materialCatalog") {
        return;
      }
      this.completeRequirement("materialOpen");
    },
    handleContainerAdded(val) {
      if (!this.visible || this.currentStep.key !== "container" || !val) {
        return;
      }
      this.completeRequirement("containerAdd");
    },
    handleFlowerChoose(val) {
      if (val && val.helperRect) {
        this.dragFocusRect = val.helperRect;
      }
      if (!this.visible || this.currentStep.key !== "flower" || !val) {
        return;
      }
      this.completeRequirement("flowerAdd");
    },
    handleFlowerMove() {
      if (!this.visible || this.currentStep.key !== "drag") {
        return;
      }
      this.completeRequirement("flowerMove");
    },
    handleToolClick(index) {
      if (!this.visible) {
        return;
      }
      this.completeRequirement(`tool-${index}`);
    },
    handleControlChange(payload) {
      if (!this.visible || !payload) {
        return;
      }
      this.completeRequirement(payload.key);
    },
    handleSelectionHelperPosition(rect) {
      this.dragFocusRect = rect || null;
      if (this.visible && this.currentStep.key === "drag") {
        this.updateTargetRect();
      }
    },
    startMeasureLoop() {
      this.stopMeasureLoop();
      this.measureTimer = setInterval(this.updateTargetRect, 450);
    },
    stopMeasureLoop() {
      if (this.measureTimer) {
        clearInterval(this.measureTimer);
        this.measureTimer = null;
      }
    },
    measureCard() {
      if (this.$refs.card) {
        this.cardHeight = this.$refs.card.offsetHeight || 176;
      }
    },
    getTargetSelectors(step) {
      const target = this.activeRequirement && this.activeRequirement.target
        ? this.activeRequirement.target
        : step.target;
      const selectors = Array.isArray(target) ? target : [target];
      return selectors.map((selector) => (
        selector === "selection-helper" ? '[data-guide="editor-canvas"]' : selector
      ));
    },
    getCustomTargetRect() {
      const target = this.activeRequirement && this.activeRequirement.target
        ? this.activeRequirement.target
        : this.currentStep.target;
      const targets = Array.isArray(target) ? target : [target];
      if (targets.includes("selection-helper") && this.dragFocusRect) {
        return this.dragFocusRect;
      }
      return null;
    },
    isVisibleElement(el) {
      if (!el) {
        return false;
      }
      const rect = el.getBoundingClientRect();
      const style = window.getComputedStyle(el);
      return (
        rect.width > 1 &&
        rect.height > 1 &&
        style.display !== "none" &&
        style.visibility !== "hidden"
      );
    },
    findTarget() {
      const selectors = this.getTargetSelectors(this.currentStep);
      for (let i = 0; i < selectors.length; i++) {
        const el = document.querySelector(selectors[i]);
        if (this.isVisibleElement(el)) {
          return el;
        }
      }
      return null;
    },
    updateTargetRect() {
      if (!this.visible) {
        return;
      }
      this.measureCard();
      const customRect = this.getCustomTargetRect();
      if (customRect) {
        const padding = 12;
        this.targetRect = {
          left: Math.max(8, customRect.left - padding),
          top: Math.max(8, customRect.top - padding),
          right: Math.min(window.innerWidth - 8, customRect.right + padding),
          width: Math.min(window.innerWidth - 16, customRect.width + padding * 2),
          height: Math.min(window.innerHeight - 16, customRect.height + padding * 2),
          bottom: Math.min(window.innerHeight - 8, customRect.bottom + padding),
        };
        return;
      }
      const target = this.findTarget();
      if (!target) {
        this.targetRect = null;
        return;
      }
      if (target.scrollIntoView) {
        target.scrollIntoView({
          block: "nearest",
          inline: "center",
        });
      }
      const rect = target.getBoundingClientRect();
      const padding = 8;
      this.targetRect = {
        left: Math.max(8, rect.left - padding),
        top: Math.max(8, rect.top - padding),
        right: Math.min(window.innerWidth - 8, rect.right + padding),
        width: Math.min(window.innerWidth - 16, rect.width + padding * 2),
        height: Math.min(window.innerHeight - 16, rect.height + padding * 2),
        bottom: Math.min(window.innerHeight - 8, rect.bottom + padding),
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.practice_guide {
  position: fixed;
  inset: 0;
  z-index: 3200;
  pointer-events: none;
}

.practice_guide_dim {
  position: absolute;
  inset: 0;
  background: rgba(20, 28, 18, 0.1);
}

.practice_guide_highlight {
  position: fixed;
  border: 2px solid #ff8a00;
  border-radius: rem(18);
  box-shadow: 0 0 0 rem(4) rgba(255, 138, 0, 0.18);
  box-sizing: border-box;
  background: transparent;
  pointer-events: none;
  overflow: visible;
  animation: guideOrangePulse 1.25s ease-in-out infinite;
}

.practice_guide.coach .practice_guide_highlight {
  border-width: 1px;
  border-style: solid;
  box-shadow: 0 0 0 rem(3) rgba(255, 138, 0, 0.14);
  background: transparent;
}

.practice_guide_card {
  position: fixed;
  padding: rem(22) rem(24) rem(20);
  border-radius: rem(18);
  background: rgba(255, 255, 255, 0.97);
  box-shadow: 0 rem(14) rem(36) rgba(24, 36, 18, 0.26);
  box-sizing: border-box;
  pointer-events: auto;
  overflow-y: auto;
}

.practice_guide.coach .practice_guide_card {
  padding: rem(16) rem(18);
  border: 1px solid rgba(218, 227, 207, 0.9);
  border-radius: rem(14);
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 rem(8) rem(20) rgba(24, 36, 18, 0.13);
  backdrop-filter: blur(5px);
}

.practice_guide_card.center {
  left: rem(28);
  right: rem(28);
  top: 50%;
  transform: translateY(-50%);
  width: auto;
}

.guide_progress {
  color: #7b856f;
  font-size: rem(22);
}

.practice_guide.coach .guide_progress {
  font-size: rem(20);
}

.guide_title {
  margin-top: rem(8);
  color: #243026;
  font-size: rem(31);
  line-height: rem(40);
  font-weight: bold;
}

.practice_guide.coach .guide_title {
  margin-top: rem(4);
  font-size: rem(26);
  line-height: rem(34);
}

.guide_text {
  margin-top: rem(10);
  color: #4e5a48;
  font-size: rem(25);
  line-height: rem(38);
}

.practice_guide.coach .guide_text {
  margin-top: rem(8);
  font-size: rem(22);
  line-height: rem(32);
}

.practice_guide.coach .guide_checks {
  display: none;
}

.guide_task {
  margin-top: rem(14);
  padding: rem(12) rem(16);
  border-radius: rem(14);
  background: #eef5de;
  color: #3f6415;
  font-size: rem(24);
  line-height: rem(34);
  font-weight: bold;
}

.practice_guide.coach .guide_task {
  margin-top: rem(8);
  padding: rem(8) rem(10);
  border-radius: rem(10);
  font-size: rem(22);
  line-height: rem(30);
}

.guide_checks {
  margin-top: rem(12);
}

.guide_check_item {
  display: flex;
  align-items: center;
  color: #64705e;
  font-size: rem(22);
  line-height: rem(32);

  + .guide_check_item {
    margin-top: rem(4);
  }

  span {
    width: rem(20);
    height: rem(20);
    margin-right: rem(10);
    border-radius: 50%;
    border: 1px solid #b8c6a2;
    box-sizing: border-box;
  }
}

.guide_check_item.complete {
  color: #6e9f21;
  font-weight: bold;

  span {
    border-color: #6e9f21;
    background: #6e9f21;
    box-shadow: inset 0 0 0 rem(4) #fff;
  }
}

.guide_status {
  margin-top: rem(12);
  color: #8a6a1f;
  font-size: rem(22);
  line-height: rem(32);
}

.practice_guide.coach .guide_status {
  margin-top: rem(8);
  font-size: rem(20);
  line-height: rem(28);
}

.guide_status.complete {
  color: #5f8f19;
}

.guide_actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: rem(18);
}

.practice_guide.coach .guide_actions {
  margin-top: rem(10);
}

.guide_actions button {
  height: rem(52);
  min-width: rem(120);
  padding: 0 rem(24);
  border-radius: rem(26);
  font-size: rem(24);
  line-height: rem(52);
  white-space: nowrap;
  border: 0;
}

.practice_guide.coach .guide_actions button {
  height: rem(42);
  min-width: rem(96);
  padding: 0 rem(16);
  border-radius: rem(21);
  font-size: rem(21);
  line-height: rem(42);
}

.practice_guide.coach .guide_next {
  min-width: rem(140);
}

.guide_skip,
.guide_cancel {
  background: transparent;
  color: #7a8274;
}

.guide_cancel {
  border: 1px solid rgba(204, 213, 194, 0.9) !important;
}

.guide_next {
  min-width: rem(170);
  margin-left: rem(12);
  background: #6e9f21;
  color: #fff;
  font-weight: bold;
  box-shadow: 0 rem(6) rem(14) rgba(110, 159, 33, 0.26);
}

.guide_next.disabled {
  background: #dbe3d2;
  color: #87907d;
  box-shadow: none;
}

@keyframes guideOrangePulse {
  0%,
  100% {
    box-shadow: 0 0 0 rem(3) rgba(255, 138, 0, 0.12);
    opacity: 0.72;
  }

  50% {
    box-shadow: 0 0 0 rem(9) rgba(255, 138, 0, 0.24);
    opacity: 1;
  }
}
</style>
